import { performRequestFourth } from 'common/apiHandler';
import apiEndPoints from 'common/apiEndPoints';
import { getTenantDetails } from 'utils/utils';

/**
 * Identity params for the 8 core contract endpoints (api-contract.yaml SP-02).
 * The API contract requires ONLY tenant_id + tenant_sk — firm_user_id is not accepted
 * by these endpoints (created_by is populated server-side from the Cognito JWT).
 */
const contractIdentityParams = () => {
  const [tenantId, tenantSk] = getTenantDetails();
  return `tenant_id=${tenantId}&tenant_sk=${tenantSk}`;
};

/**
 * Identity params for endpoints that still require firm_user_id:
 * fetchProviderOptions, fetchBodyPartOptions, and HealthLake promotion/demotion —
 * until their own contracts are published.
 * @param {string} firmUserId
 */
const identityParams = (firmUserId) => {
  const [tenantId, tenantSk] = getTenantDetails();
  return `tenant_id=${tenantId}&tenant_sk=${tenantSk}&firm_user_id=${firmUserId}`;
};

const pdfExportParams = (firmUserId) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const params = new URLSearchParams({ tenant_id: tenantId, tenant_sk: tenantSk });

  if (firmUserId) params.append('firm_user_id', firmUserId);

  return params.toString();
};

const normalizePdfExportStatus = (response) => {
  const payload = response?.data?.data || response?.data || response || {};

  return {
    status: payload.status || 'idle',
    file_id: payload.file_id,
    file_sk: payload.file_sk,
    file_path: payload.file_path,
    exported_at: payload.exported_at,
    filename: payload.filename || payload.file_name || payload.display_name,
  };
};

// ---------------------------------------------------------------------------
// Contract endpoints (tenant_id + tenant_sk only — no firm_user_id)
// ---------------------------------------------------------------------------

export const getChronology = ({ caseId, excludeFromLoading = true }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronology}?${contractIdentityParams()}&case_id=${caseId}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

export const createChronology = ({ caseId }) => {
  return performRequestFourth(apiEndPoints.methodType.POST, `${apiEndPoints.medicalChronology}?${contractIdentityParams()}`, {
    case_id: caseId,
  });
};

export const updateChronology = ({ chronologyId, payload }) => {
  return performRequestFourth(
    apiEndPoints.methodType.PATCH,
    `${apiEndPoints.medicalChronologyById(chronologyId)}?${contractIdentityParams()}`,
    payload
  );
};

/**
 * Soft-deletes a chronology and all its exhibits in a single atomic transaction.
 * Cascade is unconditional — in-flight OCR/NLP jobs are not cancelled by the FE.
 * @param {{ chronologyId: string }} params
 */
export const deleteChronology = ({ chronologyId }) => {
  return performRequestFourth(
    apiEndPoints.methodType.DELETE,
    `${apiEndPoints.medicalChronologyById(chronologyId)}?${contractIdentityParams()}`
  );
};

/**
 * Lists non-deleted exhibits for a chronology.
 * Results are ordered by exhibit_number ascending.
 * Response is a bare ExhibitFull[] — no pagination envelope.
 * Excluded from the global loader by default — WebSocket-driven refreshes and
 * Sources pagination must not flash GlobalLoader when a toast is shown.
 * @param {{ chronologyId: string, page?: number, limit?: number, excludeFromLoading?: boolean }} params
 */
export const getExhibits = ({ chronologyId, page = 1, limit = 20, excludeFromLoading = true }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyExhibits(chronologyId)}?${contractIdentityParams()}&page=${page}&limit=${limit}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

/**
 * Adds one or more files as numbered exhibits on a chronology.
 * Each file entry: { file_id (required), file_sk (optional) }.
 * provider_name is NOT accepted by this endpoint — do not include it.
 * @param {{ chronologyId: string, files: Array<{ file_id: string, file_sk?: string }> }} params
 */
export const addExhibits = ({ chronologyId, files }) => {
  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.medicalChronologyExhibits(chronologyId)}?${contractIdentityParams()}`,
    { files }
  );
};

export const deleteExhibit = ({ chronologyId, exhibitId }) => {
  return performRequestFourth(
    apiEndPoints.methodType.DELETE,
    `${apiEndPoints.medicalChronologyExhibitById(chronologyId, exhibitId)}?${contractIdentityParams()}`
  );
};

// ---------------------------------------------------------------------------
// SP-03 / SP-04 / SP-05 endpoints — contract auth: tenant_id + tenant_sk only
// ---------------------------------------------------------------------------

/**
 * Enqueues asynchronous exhibit processing (OCR/NLP pipeline).
 * Excluded from the global loader — processing runs in the background and the UI
 * should remain interactive after the exhibit record is created.
 * @param {{ chronologyId: string, exhibitId: string, excludeFromLoading?: boolean }} params
 */
export const processExhibit = ({ chronologyId, exhibitId, excludeFromLoading = true }) => {
  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.medicalChronologyProcessExhibit(chronologyId, exhibitId)}?${contractIdentityParams()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

export const getCompileStatus = ({ chronologyId, excludeFromLoading = true }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyCompileStatus(chronologyId)}?${contractIdentityParams()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

export const getOverview = ({ chronologyId, startDate, endDate, incidentFilter, page, limit, excludeFromLoading = true }) => {
  const params = new URLSearchParams(contractIdentityParams());
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (incidentFilter) params.append('incident_filter', incidentFilter);
  if (page !== undefined && page !== null) params.append('page', page);
  if (limit !== undefined && limit !== null) params.append('limit', limit);

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyOverview(chronologyId)}?${params.toString()}`,
    null,
    null,
    true,
    excludeFromLoading
  );
};

/**
 * Fetches timeline entries for a compiled chronology.
 *
 * Filter arrays must contain plain string values (already extracted from react-select
 * option objects by the FilterBar). Non-empty arrays are serialised as a single
 * comma-separated query param (e.g. `?visit_type=follow_up,specialist_visit`).
 * Empty arrays cause the corresponding param to be omitted entirely.
 *
 * Pagination is optional — omit `page` / `pageSize` to fetch all entries (Calendar tab).
 *
 * @param {{ chronologyId: string, firmUserId: string, visitTypeFilter?: string[],
 *   providerFilter?: string[], bodyPartFilter?: string[], searchQuery?: string,
 *   page?: number, pageSize?: number, startDate?: string, endDate?: string }}
 */
export const getTimeline = ({
  chronologyId,
  visitTypeFilter = [],
  providerFilter = [],
  bodyPartFilter = [],
  searchQuery = '',
  page,
  pageSize,
  startDate,
  endDate,
}) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const params = new URLSearchParams({ tenant_id: tenantId, tenant_sk: tenantSk });

  if (visitTypeFilter.length) params.append('visit_type', visitTypeFilter.join(','));
  if (providerFilter.length) params.append('provider', providerFilter.join(','));
  if (bodyPartFilter.length) params.append('body_part', bodyPartFilter.join(','));
  if (searchQuery) params.append('search', searchQuery);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (page !== undefined) params.append('page', page);
  if (pageSize !== undefined) params.append('limit', pageSize);

  return performRequestFourth(apiEndPoints.methodType.GET, `${apiEndPoints.medicalChronologyTimeline(chronologyId)}?${params.toString()}`);
};

export const getProviders = ({ chronologyId, page = 1, limit = 10, startDate, endDate }) => {
  const params = new URLSearchParams(contractIdentityParams());
  params.append('page', page);
  params.append('limit', limit);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return performRequestFourth(apiEndPoints.methodType.GET, `${apiEndPoints.medicalChronologyProviders(chronologyId)}?${params.toString()}`);
};

export const getFlags = ({ chronologyId, page = 1, limit = 10 }) => {
  const params = new URLSearchParams(contractIdentityParams());
  params.append('page', page);
  params.append('limit', limit);

  return performRequestFourth(apiEndPoints.methodType.GET, `${apiEndPoints.medicalChronologyFlags(chronologyId)}?${params.toString()}`);
};

export const getDiagnostics = ({ chronologyId, page = 1, limit = 10, startDate, endDate, incidentFilter }) => {
  const params = new URLSearchParams(contractIdentityParams());
  params.append('page', page);
  params.append('limit', limit);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (incidentFilter) params.append('incident_filter', incidentFilter);

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyDiagnostics(chronologyId)}?${params.toString()}`
  );
};

export const getSummary = ({ chronologyId, page = 1, limit = 10, startDate, endDate }) => {
  const params = new URLSearchParams(contractIdentityParams());
  params.append('page', page);
  params.append('limit', limit);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return performRequestFourth(apiEndPoints.methodType.GET, `${apiEndPoints.medicalChronologySummary(chronologyId)}?${params.toString()}`);
};

/**
 * Mints a 15-minute S3 presigned GET URL for the converted exhibit PDF.
 * The frontend appends `#page={page}` to `presigned_url` for deep-linking —
 * the backend never embeds the fragment in the URL itself.
 * @param {{ chronologyId: string, exhibitId: string, page: number }}
 */
export const getSourceLink = ({ chronologyId, exhibitId, page }) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const params = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    exhibit_id: exhibitId,
    page,
  });
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologySourceLink(chronologyId)}?${params.toString()}`
  );
};

export const startPdfExport = ({ chronologyId, firmUserId } = {}) => {
  const payload = firmUserId ? { firm_user_id: firmUserId } : undefined;

  return performRequestFourth(
    apiEndPoints.methodType.POST,
    `${apiEndPoints.medicalChronologyPdfExport(chronologyId)}?${pdfExportParams()}`,
    payload
  );
};

export const getPdfExportStatus = async ({ chronologyId, firmUserId } = {}) => {
  const response = await performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyPdfExportStatus(chronologyId)}?${pdfExportParams(firmUserId)}`
  );

  return normalizePdfExportStatus(response);
};

export const exportPdf = startPdfExport;
export const getExportPdfStatus = getPdfExportStatus;

export const getPromotionStatus = ({ fileId, firmUserId }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalRecordPromotionStatus(fileId)}?${identityParams(firmUserId)}`
  );
};

// Thin re-export until callers switch to FileManagerV2 directly (medchron-demote-actions)
export { demoteDocument } from 'modules/FileManagerV2/api/promotionService';

/**
 * Fetches a paginated page of provider options for the FilterBar AsyncPaginate.
 * @param {{ chronologyId: string, firmUserId: string, search: string, page: number, pageSize: number }}
 * @returns {{ options: Array<{label,value}>, hasMore: boolean, additional: {page} }}
 */
export const fetchProviderOptions = ({ chronologyId, firmUserId, search = '', page = 1, pageSize = 10 }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyProviderOptions(chronologyId)}?${identityParams(firmUserId)}&search=${encodeURIComponent(
      search
    )}&page=${page}&limit=${pageSize}`
  );
};

/**
 * Fetches a paginated page of body-part options for the FilterBar AsyncPaginate.
 * @param {{ chronologyId: string, firmUserId: string, search: string, page: number, pageSize: number }}
 * @returns {{ options: Array<{label,value}>, hasMore: boolean, additional: {page} }}
 */
export const fetchBodyPartOptions = ({ chronologyId, firmUserId, search = '', page = 1, pageSize = 10 }) => {
  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.medicalChronologyBodyPartOptions(chronologyId)}?${identityParams(firmUserId)}&search=${encodeURIComponent(
      search
    )}&page=${page}&limit=${pageSize}`
  );
};
